import {
    AreaChart,
    Area,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getData } from '../../../webService/webservice';
import { exportDataToExcel } from "../../../utils/excel-Export.js";
import { toast } from "react-toastify";
import { useStore } from "../../../Context/context.jsx";

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#3C0008] text-white px-4 py-3 rounded-xl shadow-xl border border-slate-700">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{payload[0].payload.label || payload[0].payload.month}</p>
                {payload.map((item) => (
                    <div key={item.dataKey} className="flex justify-between gap-4 items-center mb-1">
                        <span className="text-sm flex items-center gap-2">
                            <span style={{ backgroundColor: item.color || item.fill }} className="w-2 h-2 rounded-full"></span>
                            {item.name || item.dataKey}:
                        </span>
                        <span className="text-sm font-mono font-bold">₹{Number(item.value).toLocaleString()}</span>
                    </div>
                ))}
                <div className="mt-2 pt-2 border-t border-slate-700 flex justify-between items-center text-blue-400">
                    <span className="text-xs font-bold uppercase">Total</span>
                    <span className="text-sm font-mono font-bold">₹{(payload.reduce((acc, curr) => acc + curr.value, 0)).toLocaleString()}</span>
                </div>
            </div>
        );
    }
    return null;
};

const EarningReport = ({ sessionToken }) => {
    const { startDate, endDate } = useOutletContext();
    const { selectedStoreId } = useStore();
    const [chartData, setChartData] = useState([]);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);

            const start = new Date(startDate + "T00:00:00");
            const end = new Date(endDate + "T23:59:59");

            const params = {
                storeId: selectedStoreId,
                startDate: start.toISOString(),
                endDate: end.toISOString()
            };

            const response = await getData(
                `reports/reports/revenue`,
                params,
                sessionToken
            );

            if (response?.success === false || (response?.status && response?.status !== 200)) {
                toast.error(response?.message || "Failed to fetch Earning Report");
                return;
            }

            const monthlyData = response?.data?.monthly || [];

            const normalizedData = monthlyData.map(d => ({
                label: d.label || d.month,
                products: d.products || 0,
                services: d.services || 0,
                membership: d.membership || 0
            }));

            setChartData(normalizedData);

            const total = normalizedData.reduce(
                (acc, d) => acc + d.products + d.services + d.membership,
                0
            );
            setTotalRevenue(total);

            toast.success(response?.message || "Revenue report fetched successfully");

        } catch (error) {
            console.error("❌ Error fetching earning report:", error);
            toast.error("Something went wrong while fetching report");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchData();
    }, [selectedStoreId, startDate, endDate]);

    const handleExportExcel = () => {
        if (!chartData || !chartData.length) return;

        const data = chartData.map(d => ({
            Label: d.label,
            Products: d.products,
            Services: d.services,
            Memberships: d.membership,
            Total: d.products + d.services + d.membership
        }));

        exportDataToExcel(data, "Earning Report", `Earning_Report_${startDate}_to_${endDate}.xlsx`);
    };

    const renderChart = () => {
        const commonProps = {
            data: chartData,
            margin: { top: 20, right: 30, left: 0, bottom: 20 }
        };

        const AxisX = <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }} dy={10} />;
        const AxisY = <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }} tickFormatter={(value) => `₹${value >= 1000 ? (value / 1000) + 'k' : value}`} />;
        const Grid = <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />;
        const TooltipComp = <Tooltip content={<CustomTooltip />} />;
        const LegendComp = <Legend verticalAlign="top" align="center" height={50} iconType="circle" wrapperStyle={{ paddingBottom: '20px' }} />;

        // Handling single point data for better visualization
        const isSinglePoint = chartData.length === 1;

        return (
            <AreaChart {...commonProps}>
                <defs>
                    <linearGradient id="colorProducts" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3C0008" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#3C0008" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="colorServices" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#a52a2a" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#a52a2a" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="colorMemberships" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4d0000" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#4d0000" stopOpacity={0.05} />
                    </linearGradient>
                </defs>
                {Grid} {AxisX} {AxisY} {TooltipComp} {LegendComp}
                {isSinglePoint ? (
                    <>
                        <Bar dataKey="products" name="Products" fill="#3C0008" radius={[4, 4, 0, 0]} barSize={60} />
                        <Bar dataKey="services" name="Services" fill="#a52a2a" radius={[4, 4, 0, 0]} barSize={60} />
                        <Bar dataKey="membership" name="Memberships" fill="#4d0000" radius={[4, 4, 0, 0]} barSize={60} />
                    </>
                ) : (
                    <>
                        <Area type="monotone" dataKey="products" name="Products" stroke="#3C0008" strokeWidth={3} fillOpacity={1} fill="url(#colorProducts)" dot={{ r: 5, fill: '#3C0008', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                        <Area type="monotone" dataKey="services" name="Services" stroke="#a52a2a" strokeWidth={3} fillOpacity={1} fill="url(#colorServices)" dot={{ r: 5, fill: '#a52a2a', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                        <Area type="monotone" dataKey="membership" name="Memberships" stroke="#4d0000" strokeWidth={3} fillOpacity={1} fill="url(#colorMemberships)" dot={{ r: 5, fill: '#4d0000', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                    </>
                )}
            </AreaChart>
        );
    };

    return (
        <div className="summary-main-clean">
            {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#3C0008' }}>Earning Analytics</Typography>
                <button
                    className="export-excel-btn"
                    onClick={handleExportExcel}
                    style={{
                        justifyContent: 'center',
                        background: '#3C0008',
                        padding: '10px 24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        fontSize: '14px'
                    }}
                >
                    <Icon icon="mdi:file-excel-outline" width="20" /> Export Excel
                </button>
            </div> */}


            <div className="report-divider"></div>
            <div className="overview-card-highlight">
                <div className="overview-icon-box">
                    <Icon icon="mdi:finance" />
                </div>
                <div className="earning-info">
                    <span className="overview-info-label" style={{ fontSize: '12px', letterSpacing: '0.05em' }}>REVENUE PERFORMANCE</span>
                    <h2 className="overview-info-value" style={{ fontSize: '38px', fontWeight: 800 }}>₹ {totalRevenue.toLocaleString()}</h2>
                </div>
            </div>

            <div className="chart-wrapper-clean" style={{ padding: '32px', background: '#fff', borderRadius: '24px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.04)', border: '1px solid #f1f5f9' }}>
                {loading ? (
                    <div style={{ height: '450px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                        <span>Loading analytics...</span>
                    </div>
                ) : chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={450}>
                        {renderChart()}
                    </ResponsiveContainer>
                ) : (
                    <div style={{
                        height: '450px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#64748b',
                        gap: '12px'
                    }}>
                        <Icon icon="mdi:finance-off" width="48" style={{ opacity: 0.4 }} />
                        <span style={{ fontWeight: 600 }}>No revenue analytics found</span>
                        <p style={{ fontSize: '13px', opacity: 0.7, margin: 0 }}>There were no product, service, or membership sales recorded in this period.</p>
                    </div>
                )}
            </div>

        </div>
    );
};

export default EarningReport;
